import React from 'react';
import { useHistory, useLocation, useParams, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import moment from 'moment';
import {
  Menu as MenuIcon,
  ChevronRight,
  ExternalLink,
  Maximize,
  Minimize,
  RefreshCw,
  SparkleCircle,
  X,
} from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { Tabs, TabList, Tab } from '@lifeomic/chroma-react/components/Tabs';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { useSelector } from 'react-redux';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import {
  useActiveAccountId,
  useActiveUserId,
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
  usePolicy,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { enforce } from '@lifeomic/phc-web-toolkit/src/util/abac';
import {
  persistRecentConversation,
  RecentConversationStorageEntry,
} from '@lifeomic/phc-ui-qa-agent/src/hooks/useRecentConversations';
import { useOmnibarContext } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import { selectActiveProject } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker/selectors';
import urls from '../../../../util/urls';
import { isFountainLifeAccountId } from '../../../../util/fountainLifeAccount';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Chat } from '@lifeomic/phc-ui-qa-agent/src/components/Chat';
import { CLINICIANS_AGENT_USERNAME } from '@lifeomic/phc-ui-qa-agent/src/constants';
import { MemberChatHistory } from '@lifeomic/phc-ui-qa-agent/src/components/MemberChatHistory';
import { AttachmentViewerProvider } from '@lifeomic/phc-web-toolkit/src/components/Messaging/AttachmentViewer';
import { useIsCompositeReportEnabled } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/CompositeReport/useIsCompositeReportEnabled';
import {
  MemberExtractionsPage,
  useMemberExtractionReviewCounts,
} from '@lifeomic/phc-web-toolkit/src/components/MemberExtractions';
import { AnnualReportContent } from './AnnualReport';
import { CompositeReportContent } from './CompositeReport';
import { ActionPlanContent } from './ActionPlan';
import { ZoriReportsContent } from './AiTools';
import { DocumentsContent } from './Documents';
import { LabsContent } from './Labs';
import { OmicsExplorerContent } from './OmicsExplorer';
import { OnboardingSnapshotContent } from './OnboardingSnapshotContent';
import { JourneyReportContent } from './JourneyReportContent';
import { RegenerativeMedicineOpsContent } from './RegenerativeMedicineOpsContent';
import { PrioritiesContent } from './PrioritiesContent';
import { SurveysContent } from './Surveys';
import { memberDetailMessages } from './messages';
import { MemberSection, MEMBER_SECTION_STORAGE_KEY } from './types';
import { Link as ChromaLink } from '@lifeomic/chroma-react/components/Link';
import { useMemberDetailData } from './useMemberDetailData';
import { useMemberOutsideActiveCohort } from './useMemberOutsideActiveCohort';
import { useMemberDetailOmnibarSuggestions } from './useMemberDetailOmnibarSuggestions';
import { useMemberDetailStyles } from './styles';
import { useCurrentMemberNav } from './CurrentMemberNavContext';
import {
  getMemberDetailSectionConfig,
  getMemberDetailSectionPath,
  getVisibleMemberDetailSections,
  ZORI_CHAT_TILE_CONFIG,
} from './sections';
import clsx from 'clsx';
import { WithPlatformDefaults } from '@lifeomic/react-client';
import useService from '../../../../services/util/useService';
import SubjectsSearchService from '../../../../services/SubjectsSearchService';
import { getElationPatientId } from '../../../../util/elation';
import { MemberAppContent } from './MemberApp';
import { ClinicSurveyQuickLaunch } from './ClinicSurveyQuickLaunch';
import { MyOmeRequisitionTile } from './MyOmeRequisitionTile';
import MemberCohortMismatchBanner from './MemberCohortMismatchBanner';
import { RuleActionAuditContent } from './RuleActionAudit';
import {
  areAppBarsHidden,
  toggleAppBarsHidden,
} from '@lifeomic/phc-web-toolkit/src/util/appBarsVisibility';

const useStyles = useMemberDetailStyles;
const subjectsSearchService = new SubjectsSearchService();

const ZORI_CHAT_OPEN_STORAGE_KEY_PREFIX = 'member-detail.zori-chat-open';
const ZORI_PULSE_SCAN_AGENT = 'api-zori-pulse-scan';

const calculateAge = (member: fhir.Patient | null): number | undefined => {
  return member?.birthDate
    ? moment().diff(member.birthDate, 'years')
    : undefined;
};

const getMembershipTier = (member: fhir.Patient | null): string | undefined => {
  return member?.meta?.tag
    ?.find(
      tag =>
        tag.system === 'http://lifeomic.com/fhir/fountain-life-membership-tier',
    )
    ?.code?.toUpperCase();
};

const getZoriChatStorageKey = (memberId: string) =>
  `${ZORI_CHAT_OPEN_STORAGE_KEY_PREFIX}:${memberId}`;

const getStoredZoriChatOpenState = (memberId: string): boolean => {
  try {
    return (
      window.localStorage.getItem(getZoriChatStorageKey(memberId)) === 'true'
    );
  } catch (error) {
    return false;
  }
};

export interface MemberDetailProps {
  section?: MemberSection;
}

const MemberDetail: React.FC<MemberDetailProps> = ({ section }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { memberId } = useParams<{ memberId: string }>();
  const history = useHistory();
  const location = useLocation();
  const accountId = useActiveAccountId();
  const activeUserId = useActiveUserId();
  const activeProjectCohortId = useActiveProjectCohortId();
  const activeProjectId = useActiveProjectId();
  const hasFeature = useFeatures();
  const policy = usePolicy();
  const { track } = useMixpanel();
  const fountainLifeUXEnabled =
    hasFeature('fountainLifeUX') || isFountainLifeAccountId(accountId);
  const fountainLifePowerUserEnabled = hasFeature('fountainLifePowerUser');
  const zoriPulseScanEnabled = hasFeature('zoriPulseScan');
  const zoriPulsePrioritiesEnabled = hasFeature('zoriPulsePriorities');
  const genericCompositeReportEnabled = useIsCompositeReportEnabled();
  const canInvokeZoriPulseScan =
    enforce('invokeAgent', policy as any, {
      agent: ZORI_PULSE_SCAN_AGENT,
    }) || enforce('accessAdmin', policy as any);
  const regenerativeMedicineOpsEnabled = hasFeature('regenerativeMedicineOps');
  const memberJourneyProjectionEnabled = hasFeature(
    'viewMemberJourneyProjection',
  );
  const extractionsSectionVisible = getVisibleMemberDetailSections({
    fountainLifePowerUserEnabled,
    regenerativeMedicineOpsEnabled,
    zoriPulsePrioritiesEnabled,
    genericCompositeReportEnabled,
    memberJourneyProjectionEnabled,
  }).some(config => config.section === 'extractions');
  const extractionReviewCounts = useMemberExtractionReviewCounts(memberId, {
    immediate: false,
  });
  const executeExtractionReviewCounts = React.useRef(
    extractionReviewCounts.execute,
  );
  executeExtractionReviewCounts.current = extractionReviewCounts.execute;
  React.useEffect(() => {
    if (extractionsSectionVisible) {
      executeExtractionReviewCounts.current();
    }
  }, [extractionsSectionVisible]);
  const activeSection = section ?? 'landing';
  const isMasked = useMaskedMode();
  const activeProject = useSelector(selectActiveProject);
  const mrnConfig = activeProject?.mrnConfig;
  const [resolveMemberByIdentifier] = useService(
    subjectsSearchService.getSubjects,
  );
  const [isResolvingMemberId, setIsResolvingMemberId] = React.useState(false);
  const isElationMemberId = /^\d+$/.test(memberId);
  const { searchValue, registerSuggestions, unregisterSuggestions } =
    useOmnibarContext();
  const [showZoriChat, setShowZoriChat] = React.useState(() =>
    getStoredZoriChatOpenState(memberId),
  );
  const [chatWidth, setChatWidth] = React.useState(420);
  const [isChatFullscreen, setIsChatFullscreen] = React.useState(false);
  const [isAppChromeHidden, setIsAppChromeHidden] = React.useState(() =>
    areAppBarsHidden(),
  );
  const prevShowZoriChat = React.useRef(false);
  const [chatMode, setChatMode] = React.useState<'clinician' | 'member'>(
    'clinician',
  );
  // A member-chat message a Zori Pulse citation asked us to jump to. The nonce
  // is bumped on each request so re-clicking the same citation re-scrolls and
  // re-highlights the message even when the chat is already open on it.
  const [chatHighlight, setChatHighlight] = React.useState<{
    messageId: string;
    nonce: number;
  } | null>(null);

  const openZoriChat = React.useCallback(
    (
      entryPoint:
        | 'member_header'
        | 'member_landing'
        | 'member_pulse_quick_link',
      { resetToClinician = true }: { resetToClinician?: boolean } = {},
    ) => {
      if (!showZoriChat) {
        track('zori_chat_opened', { entryPoint });
      }
      if (resetToClinician) {
        setChatMode('clinician');
      }
      setShowZoriChat(true);
    },
    [showZoriChat, track],
  );

  const handleOpenChatMessage = React.useCallback((messageId: string) => {
    setChatMode('member');
    setShowZoriChat(true);
    setChatHighlight(previous => ({
      messageId,
      nonce: (previous?.nonce ?? 0) + 1,
    }));
  }, []);
  React.useEffect(() => {
    let isCancelled = false;

    const maybeRedirectElationMemberId = async () => {
      if (!isElationMemberId || !activeProject?.id) {
        return;
      }

      setIsResolvingMemberId(true);
      try {
        const response = await resolveMemberByIdentifier({
          projectId: activeProject.id,
          identifier: [memberId],
        });
        const entries = response?.data?.patients?.entry || [];
        const matchingEntry = entries.find(
          (entry: any) => getElationPatientId(entry?.resource) === memberId,
        );
        const resolvedMemberId =
          matchingEntry?.resource?.id || entries?.[0]?.resource?.id;

        if (!resolvedMemberId || resolvedMemberId === memberId) {
          return;
        }

        const memberBasePath = urls.app.members(memberId);
        if (!location.pathname.startsWith(memberBasePath)) {
          return;
        }

        const pathSuffix = location.pathname.slice(memberBasePath.length);
        history.replace(
          `${urls.app.members(resolvedMemberId)}${pathSuffix}${
            location.search
          }${location.hash}`,
        );
      } finally {
        if (!isCancelled) {
          setIsResolvingMemberId(false);
        }
      }
    };

    maybeRedirectElationMemberId();

    return () => {
      isCancelled = true;
    };
  }, [
    activeProject?.id,
    history,
    isElationMemberId,
    location.hash,
    location.pathname,
    location.search,
    memberId,
    resolveMemberByIdentifier,
  ]);

  React.useEffect(() => {
    if (section && section !== 'landing') {
      window.localStorage.setItem(MEMBER_SECTION_STORAGE_KEY, section);
    }
  }, [section]);

  React.useEffect(() => {
    setShowZoriChat(getStoredZoriChatOpenState(memberId));
    setIsChatFullscreen(false);
  }, [memberId]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        getZoriChatStorageKey(memberId),
        showZoriChat ? 'true' : 'false',
      );
    } catch (error) {
      // Ignore storage failures; preserving chat state is best-effort.
    }
  }, [memberId, showZoriChat]);

  React.useEffect(() => {
    if (!isChatFullscreen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChatFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChatFullscreen]);

  React.useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsAppChromeHidden(areAppBarsHidden());
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-app-bars-hidden'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const {
    member,
    memberDisplayName,
    formattedBirthDate,
    elationChartUrl,
    memberLoadError,
    memberNotFound,
    hasLoadedMember,
    isLoading,
    canSync,
    refreshEhrData,
    refreshMember,
  } = useMemberDetailData({
    accountId,
    memberId,
    isMasked,
    mrnConfig,
  });

  const cohortMismatch = useMemberOutsideActiveCohort({
    projectId: activeProjectId,
    patientId: member?.id,
    activeCohortId: activeProjectCohortId || null,
  });

  const age = calculateAge(member);
  const membershipTier = getMembershipTier(member);
  const { setMemberDisplayName } = useCurrentMemberNav();

  React.useEffect(() => {
    setMemberDisplayName(memberDisplayName ?? null);
    return () => setMemberDisplayName(null);
  }, [memberDisplayName, setMemberDisplayName]);

  useMemberDetailOmnibarSuggestions({
    fountainLifeUXEnabled,
    fountainLifePowerUserEnabled,
    regenerativeMedicineOpsEnabled,
    genericCompositeReportEnabled,
    memberJourneyProjectionEnabled,
    memberId,
    memberDisplayName,
    section: activeSection,
    formatMessage,
    searchValue,
    registerSuggestions,
    unregisterSuggestions,
    setShowZoriChat,
  });

  React.useEffect(() => {
    const shouldAddToRecent =
      showZoriChat && !prevShowZoriChat.current && member && activeUserId;
    prevShowZoriChat.current = showZoriChat;

    if (!shouldAddToRecent) return;

    const userIds = [
      activeUserId,
      CLINICIANS_AGENT_USERNAME,
      `scope?subject=${member.id}`,
    ];
    const entry: RecentConversationStorageEntry = {
      conversationId: '',
      patientId: member.id,
      title: memberDisplayName || undefined,
      lastOpenedAt: new Date().toISOString(),
      userIds,
    };

    persistRecentConversation(entry);
  }, [showZoriChat, member, activeUserId, memberDisplayName]);

  const openMemberSectionInNewTab = React.useCallback(
    (path: string) => {
      const fullUrl = `${window.location.origin}${history.createHref({
        pathname: path,
      })}`;
      window.open(fullUrl, '_blank');
    },
    [history],
  );

  if (isElationMemberId && isResolvingMemberId) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <DotLoader />
      </Box>
    );
  }

  if (!activeSection) {
    return null;
  }

  const contentMessage = (() => {
    switch (section) {
      default:
        return memberDetailMessages.contentPlaceholder;
    }
  })();

  const getMemberSectionUrl = (targetSection: MemberSection) =>
    getMemberDetailSectionPath(memberId, targetSection);

  const getSectionLabel = (targetSection: MemberSection): string => {
    const config = getMemberDetailSectionConfig(targetSection);
    return config
      ? formatMessage(memberDetailMessages[config.labelMessage])
      : '';
  };

  const visibleMemberSections = getVisibleMemberDetailSections({
    fountainLifePowerUserEnabled,
    regenerativeMedicineOpsEnabled,
    zoriPulsePrioritiesEnabled,
    genericCompositeReportEnabled,
    memberJourneyProjectionEnabled,
  });
  const sectionLabel = getSectionLabel(activeSection);

  const memberLoadReasonMessage = memberNotFound
    ? memberDetailMessages.memberNotFound
    : memberDetailMessages.memberUnavailable;

  const loadingFallback =
    isLoading || (!member && !hasLoadedMember && !memberLoadError) ? (
      <Box fullWidth fullHeight align="center" justify="center" gap={1}>
        <DotLoader />
      </Box>
    ) : (
      <Text size="subbody">{formatMessage(memberLoadReasonMessage)}</Text>
    );

  const landingTiles = visibleMemberSections.filter(
    sectionConfig => sectionConfig.showOnLanding,
  );
  const quickAccessTiles = landingTiles.filter(
    sectionConfig => sectionConfig.quickAccess,
  );
  const otherToolTiles = landingTiles.filter(
    sectionConfig => !sectionConfig.quickAccess,
  );

  const renderQuickAccessTile = (
    sectionConfig: (typeof visibleMemberSections)[number],
  ) => {
    const IconComponent = sectionConfig.icon;
    const tile = (
      <button
        type="button"
        key={sectionConfig.section}
        className={clsx(classes.landingTileBase, classes.landingQuickTile)}
        onClick={() => history.push(getMemberSectionUrl(sectionConfig.section))}
      >
        <div className={classes.landingQuickTileIconBadge}>
          <IconComponent className={classes.landingQuickTileIcon} />
        </div>
        <div className={classes.landingQuickTileContent}>
          <Text
            size="subbody"
            weight="bold"
            className={classes.landingTileTitle}
          >
            {formatMessage(memberDetailMessages[sectionConfig.labelMessage])}
          </Text>
          {sectionConfig.descriptionMessage ? (
            <Text size="caption" className={classes.landingTileDescription}>
              {formatMessage(
                memberDetailMessages[sectionConfig.descriptionMessage],
              )}
            </Text>
          ) : null}
        </div>
      </button>
    );

    return sectionConfig.requirement === 'genomics' ? (
      <EnforceAccountFeatureToggle
        accountFeature="genomics"
        key={sectionConfig.section}
      >
        {tile}
      </EnforceAccountFeatureToggle>
    ) : (
      tile
    );
  };

  const renderCompactTile = (
    sectionConfig: (typeof visibleMemberSections)[number],
  ) => {
    const IconComponent = sectionConfig.icon;
    const tile = (
      <button
        type="button"
        key={sectionConfig.section}
        className={clsx(classes.landingTileBase, classes.landingCompactTile)}
        onClick={() => history.push(getMemberSectionUrl(sectionConfig.section))}
      >
        <div className={classes.landingCompactTileIconBadge}>
          <IconComponent className={classes.landingCompactTileIcon} />
        </div>
        <div className={classes.landingCompactTileContent}>
          <Text
            size="subbody"
            weight="bold"
            className={classes.landingTileTitle}
          >
            {formatMessage(memberDetailMessages[sectionConfig.labelMessage])}
          </Text>
          {sectionConfig.descriptionMessage ? (
            <Text size="caption" className={classes.landingTileDescription}>
              {formatMessage(
                memberDetailMessages[sectionConfig.descriptionMessage],
              )}
            </Text>
          ) : null}
        </div>
        <ChevronRight className={classes.landingTileChevron} />
      </button>
    );

    return sectionConfig.requirement === 'genomics' ? (
      <EnforceAccountFeatureToggle
        accountFeature="genomics"
        key={sectionConfig.section}
      >
        {tile}
      </EnforceAccountFeatureToggle>
    ) : (
      tile
    );
  };

  const landingContent = (
    <div className={classes.landingContainer}>
      <section className={classes.landingSection}>
        <div className={classes.landingSectionHeader}>
          <Text size="body" weight="bold">
            {formatMessage(memberDetailMessages.landingQuickAccessTitle)}
          </Text>
          <Text size="caption" className={classes.landingSectionSubtitle}>
            {formatMessage(memberDetailMessages.landingQuickAccessSubtitle)}
          </Text>
        </div>
        <div className={classes.landingQuickTilesGrid}>
          {quickAccessTiles.map(renderQuickAccessTile)}
          <button
            type="button"
            className={clsx(classes.landingTileBase, classes.landingQuickTile)}
            onClick={() => openZoriChat('member_landing')}
          >
            <div className={classes.landingQuickTileIconBadge}>
              <ZORI_CHAT_TILE_CONFIG.icon
                className={classes.landingQuickTileIcon}
              />
            </div>
            <div className={classes.landingQuickTileContent}>
              <Text
                size="subbody"
                weight="bold"
                className={classes.landingTileTitle}
              >
                {formatMessage(
                  memberDetailMessages[ZORI_CHAT_TILE_CONFIG.labelMessage],
                )}
              </Text>
              <Text size="caption" className={classes.landingTileDescription}>
                {formatMessage(
                  memberDetailMessages[
                    ZORI_CHAT_TILE_CONFIG.descriptionMessage
                  ],
                )}
              </Text>
            </div>
          </button>
        </div>
      </section>
      {/* Clinic surveys sit between Quick Access and Other Tools. */}
      {memberId ? <ClinicSurveyQuickLaunch memberId={memberId} /> : null}
      {otherToolTiles.length > 0 ? (
        <section className={classes.landingSection}>
          <div className={classes.landingSectionHeader}>
            <Text size="body" weight="bold">
              {formatMessage(memberDetailMessages.landingOtherToolsTitle)}
            </Text>
            <Text size="caption" className={classes.landingSectionSubtitle}>
              {formatMessage(memberDetailMessages.landingOtherToolsSubtitle)}
            </Text>
          </div>
          <div className={classes.landingCompactTilesGrid}>
            {member ? (
              <MyOmeRequisitionTile patient={member as fhir3.Patient} />
            ) : null}
            {otherToolTiles.map(renderCompactTile)}
          </div>
        </section>
      ) : null}
    </div>
  );

  const renderSectionLink = (
    sectionConfig: (typeof visibleMemberSections)[number],
  ) => {
    const link = (
      <Link
        key={sectionConfig.section}
        to={getMemberSectionUrl(sectionConfig.section)}
        className={clsx(
          classes.menuItemLink,
          activeSection === sectionConfig.section && classes.menuItemSelected,
        )}
      >
        <MenuItem className={classes.menuItemWrapper}>
          {formatMessage(memberDetailMessages[sectionConfig.menuMessage])}
        </MenuItem>
      </Link>
    );

    return sectionConfig.requirement === 'genomics' ? (
      <EnforceAccountFeatureToggle
        accountFeature="genomics"
        key={sectionConfig.section}
      >
        {link}
      </EnforceAccountFeatureToggle>
    ) : (
      link
    );
  };

  const menuItems = visibleMemberSections.map(renderSectionLink);

  let chatWrapperClassName = classes.chatWrapper;
  if (isChatFullscreen) {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperFullscreen}`;
  } else {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperOverlay}`;
  }

  const chatModeSelector = (
    <RadioGroupMinimal
      aria-label="Chat Selector"
      name="memberDetailChatMode"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setChatMode(event.target.value as 'clinician' | 'member');
      }}
      value={chatMode}
    >
      <Radio
        value="clinician"
        label={formatMessage(memberDetailMessages.chatClinicianLabel)}
      />
      <Radio
        value="member"
        label={formatMessage(memberDetailMessages.chatMemberHistoryLabel)}
      />
    </RadioGroupMinimal>
  );

  let chatContent = null;
  if (member) {
    chatContent =
      chatMode === 'clinician' ? (
        <Chat patient={member} />
      ) : (
        <MemberChatHistory
          patient={member}
          highlightMessageId={chatHighlight?.messageId}
          highlightNonce={chatHighlight?.nonce}
        />
      );
  }

  return (
    <PageLayout
      maxWidth
      applyBackgroundCover
      classes={{
        root: clsx(
          classes.pageRoot,
          activeSection === 'extractions' && classes.extractionsPageRoot,
        ),
        layoutContainer: classes.layoutContainer,
        headerBackgroundPadding: classes.headerBackgroundPadding,
        backgroundCover: classes.backgroundCover,
        content: classes.contentArea,
      }}
      leftClassName={classes.headerPatient}
      headerCenterClassName={classes.headerIconGroup}
      headerActionsClassName={classes.headerActionsSlot}
      left={
        <Box className={classes.header}>
          {isLoading || (!member && !hasLoadedMember && !memberLoadError) ? (
            <Text size="subbody" weight="bold" className={classes.headerName}>
              {formatMessage(memberDetailMessages.loadingMember)}
            </Text>
          ) : !member ? (
            <Text size="subbody" weight="bold" className={classes.headerName}>
              {formatMessage(memberLoadReasonMessage)}
            </Text>
          ) : (
            <>
              <Box className={classes.headerName} gap={1} direction="column">
                <Text size="subbody" weight="bold">
                  {memberDisplayName ||
                    formatMessage(memberDetailMessages.memberNameUnavailable)}
                </Text>
                <Box className={classes.headerMeta}>
                  {formattedBirthDate && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      DOB: {formattedBirthDate}
                    </Text>
                  )}
                  {age !== undefined && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      Age: {age}
                    </Text>
                  )}
                  {membershipTier && (
                    <Text size="subbody" className={classes.headerMetaItem}>
                      Tier: {membershipTier}
                    </Text>
                  )}
                  {elationChartUrl && (
                    <ChromaLink
                      className={classes.headerMetaItem}
                      to={elationChartUrl}
                      newTab
                    >
                      <span className={classes.elationLinkContent}>
                        {formatMessage(memberDetailMessages.elationLink)}
                        <ExternalLink style={{ width: 14, height: 14 }} />
                      </span>
                    </ChromaLink>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      }
      headerCenter={
        isAppChromeHidden ? null : (
          <Box className={classes.menuActions}>
            {fountainLifePowerUserEnabled ? (
              <Tabs selectedTabId={activeSection} manualControl>
                <TabList
                  aria-label={formatMessage(memberDetailMessages.menuLabel)}
                  className={classes.sectionTabList}
                >
                  {visibleMemberSections.map(sectionConfig => {
                    const IconComponent = sectionConfig.icon;
                    const url = getMemberSectionUrl(sectionConfig.section);
                    const label = getSectionLabel(sectionConfig.section);
                    const toApprove =
                      sectionConfig.section === 'extractions'
                        ? extractionReviewCounts.data?.toApprove ?? 0
                        : 0;
                    const tabLabel =
                      toApprove > 0
                        ? `${label}, ${formatMessage(
                            memberDetailMessages.extractionsToApprove,
                            { count: toApprove },
                          )}`
                        : label;
                    const tab = (
                      <Tooltip key={sectionConfig.section} title={label}>
                        <span className={classes.sectionTabTooltipWrapper}>
                          <Tab
                            stopId={sectionConfig.section}
                            onClick={(e: React.MouseEvent) => {
                              if (e.metaKey || e.ctrlKey) {
                                openMemberSectionInNewTab(url);
                              } else {
                                history.push(url);
                              }
                            }}
                            className={classes.sectionTab}
                            aria-label={tabLabel}
                          >
                            <IconComponent className={classes.sectionTabIcon} />
                            {toApprove > 0 ? (
                              <Pill
                                className={classes.sectionTabBadge}
                                color="negative"
                                label={
                                  toApprove > 99 ? '99+' : String(toApprove)
                                }
                              />
                            ) : null}
                          </Tab>
                        </span>
                      </Tooltip>
                    );

                    if (sectionConfig.requirement === 'genomics') {
                      return (
                        <EnforceAccountFeatureToggle
                          accountFeature="genomics"
                          key={sectionConfig.section}
                        >
                          {tab}
                        </EnforceAccountFeatureToggle>
                      );
                    }

                    return (
                      <React.Fragment key={sectionConfig.section}>
                        {tab}
                      </React.Fragment>
                    );
                  })}
                </TabList>
              </Tabs>
            ) : (
              <Menu
                aria-label={formatMessage(memberDetailMessages.menuLabel)}
                anchorElement={
                  <ButtonUnstyled
                    aria-label={formatMessage(memberDetailMessages.menuLabel)}
                    className={classes.menuButton}
                  >
                    <MenuIcon />
                    <Text size="caption" className={classes.menuLabelText}>
                      {sectionLabel ||
                        formatMessage(memberDetailMessages.menuLabel)}
                    </Text>
                  </ButtonUnstyled>
                }
                placement="bottom"
                items={menuItems}
              />
            )}
          </Box>
        )
      }
      headerActions={
        isAppChromeHidden ? (
          <Box className={classes.headerActions}>
            <Button
              variant={isAppChromeHidden ? 'contained' : 'outlined'}
              icon={isAppChromeHidden ? X : Maximize}
              data-testid="member-detail-zori-chat-fullscreen-toggle"
              onClick={() => {
                const nextIsHidden = toggleAppBarsHidden();
                setIsAppChromeHidden(nextIsHidden);
              }}
            >
              Fullscreen
            </Button>
          </Box>
        ) : (
          <Box className={classes.headerActions}>
            <Button
              variant="outlined"
              icon={RefreshCw}
              onClick={canSync ? refreshEhrData : undefined}
              disabled={!canSync}
            >
              Sync
            </Button>
            <Button
              variant={showZoriChat ? 'contained' : 'outlined'}
              icon={SparkleCircle}
              data-testid="member-detail-zori-chat-toggle"
              onClick={() => {
                if (showZoriChat) {
                  setIsChatFullscreen(false);
                  setShowZoriChat(false);
                } else {
                  openZoriChat('member_header', { resetToClinician: false });
                }
              }}
            >
              Zori
            </Button>
            <Button
              variant={isAppChromeHidden ? 'contained' : 'outlined'}
              icon={isAppChromeHidden ? X : Maximize}
              data-testid="member-detail-zori-chat-fullscreen-toggle"
              onClick={() => {
                const nextIsHidden = toggleAppBarsHidden();
                setIsAppChromeHidden(nextIsHidden);
              }}
            >
              Fullscreen
            </Button>
          </Box>
        )
      }
    >
      <Box className={classes.detailBody}>
        {member && activeProjectCohortId && cohortMismatch.showMismatch ? (
          <Box className={classes.cohortMismatchBanner}>
            <MemberCohortMismatchBanner
              show={cohortMismatch.showMismatch}
              homeCohort={cohortMismatch.homeCohort}
              isFindingHomeCohort={cohortMismatch.isFindingHomeCohort}
            />
          </Box>
        ) : null}
        <Box className={classes.contentLayout}>
          <Box
            className={classes.mainContent}
            data-testid={`member-section-${activeSection}`}
          >
            {activeSection === 'landing' ? (
              landingContent
            ) : activeSection === 'onboarding-snapshot' ? (
              <OnboardingSnapshotContent
                member={member as WithPlatformDefaults<fhir3.Patient>}
                memberId={memberId}
                fallback={loadingFallback}
                onMemberPatientRefetched={refreshMember}
              />
            ) : activeSection === 'journey-report' ? (
              <JourneyReportContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'regenerative-medicine-ops' ? (
              <RegenerativeMedicineOpsContent
                member={member as WithPlatformDefaults<fhir3.Patient>}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'annual-report' ? (
              <AnnualReportContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'composite-report' ? (
              <CompositeReportContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'action-plan' ? (
              <ActionPlanContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'labs' ? (
              <LabsContent member={member} fallback={loadingFallback} />
            ) : activeSection === 'ai-tools' ? (
              <ZoriReportsContent
                member={member}
                memberId={memberId}
                fallback={loadingFallback}
              />
            ) : activeSection === 'rule-action-audit' ? (
              <RuleActionAuditContent memberId={memberId} />
            ) : activeSection === 'documents' ? (
              <DocumentsContent member={member} fallback={loadingFallback} />
            ) : activeSection === 'extractions' ? (
              <MemberExtractionsPage patientId={memberId} />
            ) : activeSection === 'surveys' ? (
              <SurveysContent member={member} fallback={loadingFallback} />
            ) : activeSection === 'omics-explorer' ? (
              <OmicsExplorerContent memberId={memberId} />
            ) : activeSection === 'member-app' ? (
              <MemberAppContent member={member} fallback={loadingFallback} />
            ) : activeSection === 'pulse' ? (
              <PrioritiesContent
                memberId={memberId}
                projectId={activeProjectId}
                fallback={loadingFallback}
                onOpenZoriChat={() => openZoriChat('member_pulse_quick_link')}
                onOpenChatMessage={handleOpenChatMessage}
                canZoriPulseScan={
                  zoriPulseScanEnabled && canInvokeZoriPulseScan
                }
              />
            ) : (
              <Text size="subbody">{formatMessage(contentMessage)}</Text>
            )}
          </Box>
          {showZoriChat && member && (
            <Box className={chatWrapperClassName}>
              {isChatFullscreen ? (
                <div className={classes.chatResizableFullscreen}>
                  <Box
                    className={`${classes.chatSidebar} ${classes.chatSidebarFullscreen}`}
                  >
                    <Box
                      className={classes.chatHeader}
                      align="center"
                      justify="space-between"
                      paddingX={2}
                      paddingY={1}
                    >
                      <Box align="center" gap={1}>
                        <Text size="subbody" weight="bold">
                          Zori
                        </Text>
                      </Box>
                      {chatModeSelector}
                      <Box align="center" gap={1}>
                        {fountainLifeUXEnabled && (
                          <IconButton
                            size={0}
                            aria-label="Collapse"
                            icon={Minimize}
                            onClick={() => setIsChatFullscreen(prev => !prev)}
                          />
                        )}
                        <IconButton
                          size={0}
                          aria-label="Close Zori"
                          icon={X}
                          onClick={() => setShowZoriChat(false)}
                        />
                      </Box>
                    </Box>
                    <Box className={classes.chatContent}>
                      <AttachmentViewerProvider>
                        {chatContent}
                      </AttachmentViewerProvider>
                    </Box>
                  </Box>
                </div>
              ) : (
                <Resizable
                  width={chatWidth}
                  height={0}
                  onResize={(_, { size }) => setChatWidth(size.width)}
                  resizeHandles={['w']}
                  minConstraints={[320, 0]}
                  maxConstraints={[640, 0]}
                  handle={<div className={classes.resizeHandle} />}
                >
                  <div
                    className={classes.chatResizable}
                    style={{ width: chatWidth }}
                  >
                    <Box className={classes.chatSidebar}>
                      <Box
                        className={classes.chatHeader}
                        align="center"
                        justify="space-between"
                        paddingX={2}
                        paddingY={1}
                      >
                        <Box align="center" gap={1}>
                          <Text size="subbody" weight="bold">
                            Zori
                          </Text>
                        </Box>
                        {chatModeSelector}
                        <Box align="center" gap={1}>
                          {fountainLifeUXEnabled && (
                            <IconButton
                              size={0}
                              aria-label="Expand"
                              icon={Maximize}
                              onClick={() => setIsChatFullscreen(true)}
                            />
                          )}
                          <IconButton
                            size={0}
                            aria-label="Close Zori"
                            icon={X}
                            onClick={() => setShowZoriChat(false)}
                          />
                        </Box>
                      </Box>
                      <Box className={classes.chatContent}>
                        <AttachmentViewerProvider>
                          {chatContent}
                        </AttachmentViewerProvider>
                      </Box>
                    </Box>
                  </div>
                </Resizable>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

MemberDetail.displayName = 'MemberDetail';
export default MemberDetail;
